import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { SidePanelModalComponent } from 'src/app/ui-kit/side-panel-modal';
import { HelpContentComponent } from './help-content.component';

/**
 * Help drawer. Renders a small icon-button trigger that slides a side panel in
 * from the right edge with longer-form help content. Built on
 * `app-side-panel-modal`, so it teleports to `document.body` and is full-height
 * on every screen size. The panel header shows `title`; `subtitle` and any
 * Vimeo video render inside the scrollable body above the projected content.
 *
 * @example
 * <app-help-drawer title="Understanding progress" icon="information-circle-outline">
 *   <p>Longer explanation…</p>
 * </app-help-drawer>
 *
 * @feature shared
 */
@Component({
  selector: 'app-help-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, SidePanelModalComponent, HelpContentComponent],
  host: { class: 'inline-flex' },
  template: `
    <app-icon-button
      [icon]="icon()"
      [ariaLabel]="ariaLabel()"
      color="info"
      size="xxs"
      (click)="open()"
    />

    <app-side-panel-modal [isOpen]="isOpen()" [title]="title() ?? ''" (onDismiss)="isOpen.set(false)">
      <app-help-content [subtitle]="subtitle()" [videoUrl]="videoUrl()" [active]="isOpen()">
        <ng-content />
      </app-help-content>
    </app-side-panel-modal>
  `,
})
export class HelpDrawerComponent {
  /** Optional heading shown in the drawer header. */
  readonly title = input<string>();

  /** Optional secondary line shown at the top of the body. */
  readonly subtitle = input<string>();

  /** Optional Vimeo reference rendered as a player above the body. */
  readonly videoUrl = input<string>();

  /** Trigger icon filename from `assets/icons/`. @default 'help-circle-outline' */
  readonly icon = input<string>('help-circle-outline');

  /** Accessible label for the icon-only trigger. @default 'Help' */
  readonly ariaLabel = input<string>('Help');

  /** Whether the drawer is currently open. */
  readonly isOpen = signal(false);

  open(): void {
    this.isOpen.set(true);
  }
}
